import { startTransition, useState } from 'react'
import { Accordion, AccordionProps, ActionIcon, Group, MantineStyleProps, Text, Tooltip } from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import { TbCalendarTime, TbChevronLeft, TbChevronRight } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'
import { DateRangePicker } from '@/components/DateRangePicker'
import { formatDateRangeLabel, shiftDateRange } from '@/utils/dateRange'

export function DateRangeAccordion({
  startDate,
  endDate,
  onRangeChange,
  onRangeSelectionStateChange,
  variant = 'filled',
  mb = 'sm',
}: {
  startDate: Date
  endDate: Date
  onRangeChange: (startDate: Date, endDate: Date) => void
  onRangeSelectionStateChange?: (isRangeSelectionInProgress: boolean) => void
  variant?: AccordionProps['variant']
  mb?: MantineStyleProps['mb']
}) {
  const { t } = useTranslation('common')
  const [accordionValue, setAccordionValue] = useLocalStorage<string | null>({
    key: 'dateRangePickerAccordionOpen',
    defaultValue: null,
    getInitialValueInEffect: false,
  })
  // Shifting while the calendar holds a half-picked range would leave the page
  // waiting for a selection that the URL change silently discards.
  const [isRangeSelectionInProgress, setIsRangeSelectionInProgress] = useState(false)

  const handleRangeSelectionStateChange = (inProgress: boolean) => {
    setIsRangeSelectionInProgress(inProgress)
    onRangeSelectionStateChange?.(inProgress)
  }

  // Keep the current page visible while the shifted range loads instead of
  // falling back to the Suspense loader on every click.
  const shiftRange = (direction: 1 | -1) => {
    const [nextStart, nextEnd] = shiftDateRange(startDate, endDate, direction)
    startTransition(() => onRangeChange(nextStart, nextEnd))
  }

  const shiftedRangeLabel = (direction: 1 | -1) =>
    formatDateRangeLabel(...shiftDateRange(startDate, endDate, direction))

  return (
    <Accordion variant={variant} radius="md" mb={mb} value={accordionValue} onChange={setAccordionValue}>
      <Accordion.Item value="dateRange">
        <Group gap="xs" wrap="nowrap" ps="sm">
          <Tooltip label={`${t('previousInterval')}: ${shiftedRangeLabel(-1)}`}>
            <ActionIcon
              variant="default"
              size="lg"
              onClick={() => shiftRange(-1)}
              disabled={isRangeSelectionInProgress}
              aria-label={t('previousInterval')}
            >
              <TbChevronLeft size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={`${t('nextInterval')}: ${shiftedRangeLabel(1)}`}>
            <ActionIcon
              variant="default"
              size="lg"
              onClick={() => shiftRange(1)}
              disabled={isRangeSelectionInProgress}
              aria-label={t('nextInterval')}
            >
              <TbChevronRight size={18} />
            </ActionIcon>
          </Tooltip>
          <Accordion.Control icon={<TbCalendarTime size={22} color="var(--mantine-color-dimmed)" />}>
            <Text fw={500}>{formatDateRangeLabel(startDate, endDate)}</Text>
          </Accordion.Control>
        </Group>
        <Accordion.Panel>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onRangeChange={(start, end) => start && end && onRangeChange(start, end)}
            onRangeSelectionStateChange={handleRangeSelectionStateChange}
            mb="xl"
          />
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  )
}
